import { useCallback, useEffect, useRef, useState } from "react";

type Options = {
  threshold?: number;        // リフレッシュ発火閾値（デフォルト: 72px）
  maxPull?: number;          // 最大引き量（デフォルト: 120px）
  onRefresh: () => Promise<void> | void;
  onError?: (error: Error) => void;
};

type PullState = {
  startY: number | null;
  pulling: boolean;
  scrolling: boolean;
  scrollTimeout: NodeJS.Timeout | null;
  currentOffset: number;  // 現在のオフセット値を追跡
};

export function usePullToReload({
  threshold = 72,  // 引っ張る閾値
  maxPull = 120,   // 最大引き量
  onRefresh,
  onError
}: Options) {
  const ref = useRef<HTMLElement | null>(null);
  const state = useRef<PullState>({
    startY: null,
    pulling: false,
    scrolling: false,
    scrollTimeout: null,
    currentOffset: 0  // 初期値0
  });
  const refreshingRef = useRef(false);  // 即座に反映される状態管理用
  const [refreshing, setRefreshing] = useState(false);  // UI表示用

  // 内側のコンテンツ要素に直接transformを適用
  const setOffset = useCallback((value: number, animate: boolean = false) => {
    if (!ref.current) return;

    // オフセット値を状態に保存
    state.current.currentOffset = value;

    // 内側のコンテンツラッパーを探す（will-change-transformクラスを持つ要素）
    const contentWrapper = ref.current.querySelector('.will-change-transform') as HTMLElement;

    if (contentWrapper) {
      // アニメーション設定
      // animate=trueの場合、または0に戻る時はアニメーション付き
      const transition = animate || value === 0
        ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        : "none";

      // 直接スタイルを適用
      contentWrapper.style.transform = `translateY(${value}px)`;
      contentWrapper.style.transition = transition;
    } else {
      // フォールバック: CSS変数を使用（互換性のため）
      ref.current.style.setProperty("--pull-offset", `${value}px`);
      const transition = animate || value === 0
        ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        : "none";
      ref.current.style.setProperty("--pull-transition", transition);
    }
  }, []);

  // refreshing状態を同期的に開始
  const startRefreshing = useCallback(() => {
    refreshingRef.current = true;  // 即座に反映
    setRefreshing(true);  // UI表示用
  }, []);

  // refreshing状態を同期的に終了
  const stopRefreshing = useCallback(() => {
    refreshingRef.current = false;  // 即座に反映
    setRefreshing(false);  // UI表示用
  }, []);

  // 状態リセット
  const reset = useCallback(() => {
    state.current.startY = null;
    state.current.pulling = false;
    state.current.currentOffset = 0;  // オフセットもリセット
    setOffset(0);
  }, [setOffset]);

  // Pull可能かチェック（シンプルな判定）
  const canPull = useCallback((el: HTMLElement): boolean => {
    return (
      el.scrollTop <= 1 &&           // iOS対策で1px許容
      !state.current.scrolling &&    // 慣性スクロール中は不可
      !refreshingRef.current         // refreshingRef.currentを使用
    );
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // CSS変数の初期化
    el.style.setProperty("--pull-offset", "0px");
    el.style.setProperty("--pull-transition", "none");

    // タッチ開始
    const handleTouchStart = (e: TouchEvent) => {
      if (!canPull(el)) {
        reset();
        return;
      }

      state.current.startY = e.touches[0].clientY;
      state.current.pulling = false;
    };

    // タッチ移動
    const handleTouchMove = (e: TouchEvent) => {
      if (state.current.startY === null) return;

      const currentY = e.touches[0].clientY;
      const dy = currentY - state.current.startY;

      // 上方向ドラッグまたはスクロール位置が変わったらリセット
      if (dy < 0 || el.scrollTop > 1) {
        reset();
        return;
      }

      // 少しでも引いたら開始（0pxから開始）
      if (dy > 0) {
        state.current.pulling = true;

        // 0-20pxの範囲では1:1で追従（スムーズな導入）
        if (dy <= 20) {
          setOffset(dy);  // 指の動きに完全追従
        } else {
          // 20px以降はイージング適用（よりスムーズな計算）
          const directZone = 20;  // 直接追従ゾーン
          const adjustedDy = dy - directZone;
          const availableRange = threshold - directZone;
          const progress = Math.min(adjustedDy / availableRange, 2);

          // Cubic ease-out: より自然な減速カーブ
          const easedProgress = 1 - Math.pow(1 - Math.min(progress, 1), 3);
          const easedOffset = directZone + Math.min(
            maxPull - directZone,
            availableRange * easedProgress
          );

          setOffset(easedOffset);
        }

        // スクロールを防止
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    };

    // タッチ終了
    const handleTouchEnd = async () => {
      if (!state.current.pulling) {
        reset();
        return;
      }

      // state.currentから現在のオフセット値を取得（CSS変数ではなく）
      const currentOffset = state.current.currentOffset;

      if (currentOffset >= threshold && !refreshingRef.current) {
        // YouTubeスタイル: リフレッシュ中は帯の高さ（48px）で固定
        state.current.pulling = false;
        startRefreshing();  // refreshingRef.currentを即座にtrueにする
        setOffset(48, true);  // アニメーション付きで48pxに移動

        try {
          await onRefresh();
        } catch (err) {
          console.error("Pull to refresh error:", err);
          if (onError && err instanceof Error) {
            onError(err);
          }
        } finally {
          // 完了後、少し遅延してから戻る（余韻を残す）
          await new Promise(resolve => setTimeout(resolve, 300));

          // 状態を必ずクリーンアップ
          stopRefreshing();  // refreshingRef.currentを即座にfalseにする
          reset();  // アニメーション付きでリセット（自然に戻る）
        }
      } else {
        reset();  // 通常のリセット（アニメーションあり）
      }
    };

    // スクロールイベント（慣性検出）
    const handleScroll = () => {
      state.current.scrolling = true;

      // 既存のタイムアウトをクリア
      if (state.current.scrollTimeout) {
        clearTimeout(state.current.scrollTimeout);
      }

      // 200ms後に慣性終了と判定
      state.current.scrollTimeout = setTimeout(() => {
        state.current.scrolling = false;
      }, 200);

      // スクロール位置が0より大きい場合はリセット
      if (el.scrollTop > 1) {
        reset();
      }
    };

    // イベントリスナーの登録
    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    el.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    el.addEventListener("scroll", handleScroll, { passive: true });

    // クリーンアップ
    return () => {
      if (state.current.scrollTimeout) {
        clearTimeout(state.current.scrollTimeout);
      }

      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
      el.removeEventListener("scroll", handleScroll);
    };
  }, [onRefresh, threshold, maxPull, onError]);

  // offsetを返す（stateから取得）
  const getOffset = useCallback(() => {
    return state.current.currentOffset;
  }, []);

  return {
    ref,
    refreshing,
    isReady: getOffset() >= threshold,  // 離すと更新できる状態
  };
}